import { useMemo, useRef } from 'react';

import { useAccountFeatures, usePermissions } from '@dbt-labs/app-nav';

import { getCostInsightsUiState, type MetricVisibility } from './costInsightsUiState';
import { FeatureFlags } from './types';
import { useFusionSaoValidations } from './useFusionSaoValidations';

export type { MetricVisibility } from './costInsightsUiState';

export type UseCostInsightsEnablementArgs = {
  accountId: number;
  environmentId: number;
  /**
   * Optional; forwarded to `useFusionSaoValidations`. If omitted there, it will be derived
   * from the environment response.
   */
  projectId?: number;
  featureFlags: FeatureFlags;
  /**
   * Optional. If true, pauses all data fetching queries.
   */
  pause?: boolean;
};

export type CostInsightsEnablement = {
  // Fusion / SAO
  fusionEligibility: ReturnType<typeof useFusionSaoValidations>['fusionEligibility'];
  isFusionEligibleForProject: boolean;
  canEnableFusion: boolean;
  isUsingFusion: boolean;
  isSaoEligible: boolean;
  isSaoEnabledForProject: boolean;
  totalJobs: number;
  jobsWithSAOEnabled: number;

  // Cost Insights — primary gates
  /**
   * Whether cost insights is enabled at the account level (account feature + feature flag).
   * Use this for account-scoped checks and banner logic.
   */
  hasAccountCostFeatureAccess: boolean;
  /**
   * Whether this project has full cost feature access (account + FF + fusion eligible).
   * Gates the entire cost insights dashboard section.
   */
  hasProjectCostFeatureAccess: boolean;
  /**
   * Whether this user can view cost-specific data (account feature + FF + fusion eligible + SAO eligible + permission).
   * Gates the Cost tab, cost charts, and overview metrics.
   */
  canViewProjectCostData: boolean;
  /**
   * Whether this user can view non-cost data for this project (Usage, Query run time, Model builds).
   * Requires project-level cost feature access (account feature + FF + fusion eligible + SAO eligible)
   * but NOT cost_insights_read permission, since this data is not cost-specific.
   */
  canViewProjectNonCostData: boolean;

  /**
   * Derived metric visibility based on the full platform state matrix.
   * - 'full': All cost-related tabs and data visible
   * - 'partial': Usage/query tabs visible but not cost insights
   * - 'none': No cost-related tabs visible
   *
   * Returns 'none' when the feature flag is disabled or data is still loading.
   */
  metricVisibility: MetricVisibility;

  isLoading: boolean;
};

/**
 * Hook to compute the enablement state for the Cost Insights feature.
 *
 * Primary gates (use these for rendering decisions):
 * - hasAccountCostFeatureAccess: account feature + FF
 * - hasProjectCostFeatureAccess: account + FF + fusion eligible
 * - canViewProjectCostData: project access + fusion + SAO eligible + permission (gates cost-specific UI)
 * - canViewProjectNonCostData: project access + fusion + SAO eligible (gates non-cost tabs)
 *
 * Sub-checks (for progressive enablement banners):
 * - isFusionEligibleForProject, isUsingFusion, isSaoEnabledForProject, canEnableFusion, fusionEligibility
 */
export const useCostInsightsEnablement = ({
  accountId,
  environmentId,
  projectId,
  featureFlags,
  pause,
}: UseCostInsightsEnablementArgs): CostInsightsEnablement => {
  const { meta5835EnableCostEstimationFrontend } = featureFlags;

  // Conditionally call hooks based on pause - pass undefined accountId when paused
  const validAccountId = pause ? undefined : accountId;
  const validProjectId = pause ? undefined : projectId;
  const accountFeaturesQuery = useAccountFeatures(validAccountId);
  const permissions = usePermissions(validAccountId, validProjectId);

  const {
    fusionEligibility,
    isFusionEligibleForProject,
    canEnableFusion,
    isUsingFusion,
    isSaoEligible,
    isSaoEnabledForProject,
    totalJobs,
    jobsWithSAOEnabled,
    isLoading: isFusionSaoLoading,
  } = useFusionSaoValidations({
    accountId,
    environmentId,
    projectId,
    featureFlags,
    pause,
  });

  const isAccountFeatureEnabled = !!accountFeaturesQuery.data?.costInsights;
  const isFeatureFlagEnabled = !!meta5835EnableCostEstimationFrontend;
  const hasCostInsightsPermission = !!permissions.permissions?.cost_insights_read;

  const hasAccountCostFeatureAccess = useMemo(() => {
    return isAccountFeatureEnabled && isFeatureFlagEnabled;
  }, [isAccountFeatureEnabled, isFeatureFlagEnabled]);

  const hasProjectCostFeatureAccess = useMemo(() => {
    return hasAccountCostFeatureAccess && isFusionEligibleForProject;
  }, [hasAccountCostFeatureAccess, isFusionEligibleForProject]);

  const canViewProjectCostData = useMemo(() => {
    return hasProjectCostFeatureAccess && hasCostInsightsPermission && isSaoEligible;
  }, [hasProjectCostFeatureAccess, hasCostInsightsPermission, isSaoEligible]);

  const canViewProjectNonCostData = useMemo(() => {
    return hasProjectCostFeatureAccess && isSaoEligible;
  }, [hasProjectCostFeatureAccess, isSaoEligible]);

  const isLoading =
    accountFeaturesQuery.isLoading || permissions.isLoading || isFusionSaoLoading;

  // Derive metric visibility from the full state matrix, gated by the feature flag.
  const metricVisibility: MetricVisibility =
    !isFeatureFlagEnabled || isLoading
      ? 'none'
      : getCostInsightsUiState({
          isSaoEligible,
          isUsingFusion,
          isSaoEnabledForProject,
          isCostInsightsFeatureEnabled: hasAccountCostFeatureAccess,
          hasCostInsightsPermission,
          canEnableFusion,
        }).metricVisibility;

  const currentResult: CostInsightsEnablement = {
    fusionEligibility,
    isFusionEligibleForProject,
    canEnableFusion,
    isUsingFusion,
    isSaoEligible,
    isSaoEnabledForProject,
    totalJobs,
    jobsWithSAOEnabled,
    hasAccountCostFeatureAccess,
    hasProjectCostFeatureAccess,
    canViewProjectCostData,
    canViewProjectNonCostData,
    metricVisibility,
    isLoading,
  };

  // Cache the last settled result so that when params change and queries
  // refetch (data briefly becomes undefined), consumers keep seeing the
  // previous values instead of a flash of falsy defaults.
  const lastStableResult = useRef<CostInsightsEnablement | null>(null);

  if (!isLoading) {
    lastStableResult.current = currentResult;
  }

  if (isLoading && lastStableResult.current) {
    return { ...lastStableResult.current, isLoading: true };
  }

  return currentResult;
};
